import { Info } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { INTERACTIVE_EDITOR_DEFAULT_SCHEMA } from "./constants";
import { useSchemaEditorLayout } from "./schema-editor-layout-context";

import { useSchemaEditor } from ".";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface ComplexSchemaEditorBannerProps {
  /** Shorter copy when the interactive tree could not load at all. */
  variant?: "composition" | "unavailable";
}

const ComplexSchemaEditorBanner = observer(
  ({ variant = "composition" }: ComplexSchemaEditorBannerProps) => {
    const schemaEditorStore = useSchemaEditor();
    const { openJsonEditor } = useSchemaEditorLayout();
    const [confirmOpen, setConfirmOpen] = useState(false);

    if (!schemaEditorStore.usesComplexJsonSchema || schemaEditorStore.isReadonly) {
      return null;
    }

    const handleReset = () => {
      schemaEditorStore.resetToInteractiveDefault(INTERACTIVE_EDITOR_DEFAULT_SCHEMA);
      setConfirmOpen(false);
    };

    return (
      <>
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/80 p-4 dark:border-blue-900 dark:bg-blue-950/30">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3">
              <Info
                className="mt-0.5 h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300"
                aria-hidden
              />
              <p className="text-sm text-blue-950 dark:text-blue-100">
                {variant === "unavailable"
                  ? "This schema can't be shown in the interactive editor."
                  : "You're using a complex schema."}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button type="button" variant="default" size="sm" onClick={openJsonEditor}>
                Edit as JSON
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-blue-300 bg-white text-blue-950 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-50 dark:hover:bg-blue-900"
                onClick={() => setConfirmOpen(true)}
              >
                Reset to default
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset schema to default?</AlertDialogTitle>
              <AlertDialogDescription>
                Replace this schema with a simple empty object you can edit fully in the interactive
                editor. Advanced JSON Schema features will be removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReset}>Reset to default</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  },
);

export default ComplexSchemaEditorBanner;
